import { useState } from "react";
import { PHASE_LABEL } from "@/components/session";
import { Button, ErrorNote, Field, inputClass } from "@/components/ui";
import { ApiError, api, errorMessage } from "@/lib/api";
import { dateTime, money, percent, signClass } from "@/lib/format";
import { useCycleRebalances, useRefreshDirectory } from "@/lib/queries";
import { decimalInput } from "@/lib/units";
import type { NetRebalanceOutcome, NetRebalanceRow, NetRebalanceSheet } from "@/lib/types";

/*
 * I RIBILANCIAMENTI DEL NETTO: le voci che la dashboard non vede.
 *
 * Il valore inserito a mano vale come legge, e qui si vede sempre separato dal
 * calcolato: «di cui ribilanciamenti». Non si modifica — si elimina, col
 * motivo, e si reinserisce. Il CLIENT non arriva qui: la rotta non è nella sua
 * allowlist, e la sezione non si monta per lui.
 *
 * Ogni scrittura passa da un'anteprima che è la funzione vera del backend,
 * annullata: sul ciclo chiuso dice il netto nuovo, lo stato e che cosa
 * succede allo split, e le quote si confermano lì.
 */

type Confirm =
  | { op: "ADD"; amount: string; cause: string; manualFxRate?: string; preview: NetRebalanceOutcome }
  | { op: "REMOVE"; id: string; reason: string; preview: NetRebalanceOutcome };

export function RebalanceSection({ cycleId, sessionId, canWrite }: { cycleId: string; sessionId?: string; canWrite: boolean }) {
  const q = useCycleRebalances(cycleId);
  const [adding, setAdding] = useState(false);

  return (
    <section>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-medium text-ink">{sessionId ? "Ribilanciamenti della fase" : "Ribilanciamenti del netto"}</h2>
        {canWrite && q.data && !adding && (
          <Button size="sm" onClick={() => setAdding(true)}>
            Aggiungi ribilanciamento
          </Button>
        )}
      </div>
      {q.isLoading ? (
        <p className="card px-4 py-4 text-sm text-muted">Caricamento…</p>
      ) : q.error ? (
        <ErrorNote>{errorMessage(q.error)}</ErrorNote>
      ) : !q.data ? null : (
        <Sheet data={q.data} sessionId={sessionId} canWrite={canWrite} adding={adding} onAdded={() => setAdding(false)} />
      )}
    </section>
  );
}

function Sheet({ data, sessionId, canWrite, adding, onAdded }: { data: NetRebalanceSheet; sessionId?: string; canWrite: boolean; adding: boolean; onAdded: () => void }) {
  const cur = data.currency;
  const rows = sessionId ? data.rows.filter((r) => r.sessionId === sessionId) : data.rows;
  const fase = sessionId ? data.phases.find((p) => p.sessionId === sessionId) : undefined;
  const totale = sessionId ? (data.totals.live.bySession[sessionId] ?? 0) : (data.totals.frozen ?? data.totals.live.total);

  return (
    <div className="space-y-3">
      <div className="card flex flex-wrap gap-x-6 gap-y-2 px-4 py-3 text-sm">
        {fase && (
          <span title="Un dettaglio: fee, payout e rettifiche stanno sul ciclo, non sulle fasi">
            <span className="text-xs text-faint">Netto della fase </span>
            {fase.net === null ? (
              <span className="text-faint">{fase.source === null ? "non ancora: la fase non ha periodi chiusi" : "—"}</span>
            ) : (
              <span className={signClass(fase.net)}>{money(fase.net, cur)}</span>
            )}
            {fase.computed !== null && (
              <span className="text-muted">
                {" "}
                · calcolato {money(fase.computed, cur)} {fase.source === "DEALS" ? "(dai deal)" : "(dai periodi)"}
              </span>
            )}
          </span>
        )}
        <span>
          <span className="text-xs text-faint">{sessionId ? "Ribilanciamenti della fase " : data.frozen ? "Di cui ribilanciamenti, nel netto congelato " : "Ribilanciamenti "}</span>
          <span className={signClass(totale)}>{money(totale, cur)}</span>
          {!data.frozen && <span className="text-muted"> · entreranno nel netto alla chiusura</span>}
        </span>
      </div>

      {!sessionId && data.totals.mismatch !== null && (
        <p className="rounded border border-warn/40 bg-warn/5 px-4 py-2 text-sm text-ink">
          Le righe visibili sommano {money(data.totals.live.total, cur)}, il netto congelato ne contiene {money(data.totals.frozen, cur)}: una fase eliminata
          prima della chiusura e ripristinata dopo. Scarto {money(data.totals.mismatch, cur)}.
        </p>
      )}

      {adding && <AddForm cycleId={data.cycleId} sessionId={sessionId} onDone={onAdded} />}

      {rows.length === 0 ? (
        <p className="card px-4 py-4 text-sm text-muted">
          Nessun ribilanciamento. {canWrite ? "Aggiungine uno per una voce che la dashboard non vede: importo in USD con segno, e la causa." : ""}
        </p>
      ) : (
        <div className="card divide-y divide-line">
          {rows.map((r) => (
            <Row key={r.id} cycleId={data.cycleId} r={r} cur={cur} showPhase={!sessionId} canWrite={canWrite} />
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ cycleId, r, cur, showPhase, canWrite }: { cycleId: string; r: NetRebalanceRow; cur: string; showPhase: boolean; canWrite: boolean }) {
  const [removing, setRemoving] = useState(false);
  return (
    <div className="space-y-2 px-4 py-3 text-sm">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className={signClass(Number(r.amount))}>{money(r.amount, r.currency)}</span>
        {r.currency !== cur && <span className="text-muted">= {money(r.amountCycle, cur)} al tasso {Number(r.fxRateCycle)}</span>}
        {showPhase && <span className="text-muted">{r.phase ? PHASE_LABEL(r.phase) : "Ciclo"}</span>}
        <span className="text-ink">{r.cause}</span>
        <span className="text-faint">
          {r.createdBy.name} · {dateTime(r.createdAt)}
        </span>
        {r.sessionDeleted && <span className="text-faint">fase eliminata: fuori dal netto</span>}
        {canWrite && !r.sessionDeleted && !removing && (
          <button type="button" className="ml-auto text-xs text-muted hover:text-loss hover:underline" onClick={() => setRemoving(true)}>
            Elimina
          </button>
        )}
      </div>
      {removing && <RemoveForm cycleId={cycleId} id={r.id} onDone={() => setRemoving(false)} />}
    </div>
  );
}

function AddForm({ cycleId, sessionId, onDone }: { cycleId: string; sessionId?: string; onDone: () => void }) {
  const [amount, setAmount] = useState("");
  const [cause, setCause] = useState("");
  const [rate, setRate] = useState("");
  const [askRate, setAskRate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  const preview = async () => {
    const negativo = amount.trim().startsWith("-");
    const v = decimalInput(amount.trim().replace(/^[-+]/, ""));
    if (v === null || Number(v) === 0) return setError("Scrivi l'importo in USD con il segno, per esempio -120.50");
    if (cause.trim().length < 3) return setError("Scrivi la causa del ribilanciamento");
    const manualFxRate = askRate ? (decimalInput(rate) ?? undefined) : undefined;
    if (askRate && !manualFxRate) return setError("Scrivi il tasso USD verso la valuta del ciclo");
    const firmato = negativo ? `-${v}` : v;
    setBusy(true);
    setError(null);
    try {
      const p = await api.cycles.previewRebalance(cycleId, { op: "ADD", amount: firmato, cause: cause.trim(), sessionId: sessionId ?? null, manualFxRate });
      setConfirm({ op: "ADD", amount: firmato, cause: cause.trim(), manualFxRate, preview: p });
    } catch (e) {
      if (e instanceof ApiError && e.code === "FX_RATE_MISSING") setAskRate(true);
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (confirm) return <ConfirmPanel cycleId={cycleId} sessionId={sessionId} c={confirm} onBack={() => setConfirm(null)} onDone={onDone} />;

  return (
    <div className="max-w-xl space-y-2 rounded border border-line bg-raised/50 p-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Importo (USD, con segno)" hint="Positivo aumenta il netto, negativo lo riduce. Vale come legge.">
          {(id) => <input id={id} inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} className={inputClass} placeholder="-120.50" />}
        </Field>
        <Field label="Causa" hint="Obbligatoria: è quello che fra un anno spiega questo numero.">
          {(id) => <input id={id} value={cause} onChange={(e) => setCause(e.target.value)} className={inputClass} />}
        </Field>
        {askRate && (
          <Field label="Tasso USD → valuta del ciclo" hint="Il database non ce l'ha: si congela sulla riga.">
            {(id) => <input id={id} inputMode="decimal" value={rate} onChange={(e) => setRate(e.target.value)} className={inputClass} />}
          </Field>
        )}
      </div>
      <ErrorNote>{error}</ErrorNote>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Annulla
        </Button>
        <Button size="sm" variant="primary" disabled={busy} onClick={() => void preview()}>
          {busy ? "Calcolo…" : "Anteprima"}
        </Button>
      </div>
    </div>
  );
}

function RemoveForm({ cycleId, id, onDone }: { cycleId: string; id: string; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  const preview = async () => {
    if (reason.trim().length < 3) return setError("Scrivi il motivo dell'eliminazione");
    setBusy(true);
    setError(null);
    try {
      const p = await api.cycles.previewRebalance(cycleId, { op: "REMOVE", id, reason: reason.trim() });
      setConfirm({ op: "REMOVE", id, reason: reason.trim(), preview: p });
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (confirm) return <ConfirmPanel cycleId={cycleId} c={confirm} onBack={() => setConfirm(null)} onDone={onDone} />;

  return (
    <div className="max-w-xl space-y-2 rounded border border-line bg-raised/50 p-3">
      <Field label="Motivo dell'eliminazione" hint="Obbligatorio: la riga intera resta nell'audit, con questo motivo.">
        {(fid) => <input id={fid} value={reason} onChange={(e) => setReason(e.target.value)} className={inputClass} />}
      </Field>
      <ErrorNote>{error}</ErrorNote>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Annulla
        </Button>
        <Button size="sm" variant="primary" disabled={busy} onClick={() => void preview()}>
          {busy ? "Calcolo…" : "Anteprima"}
        </Button>
      </div>
    </div>
  );
}

const STATE_LABEL: Record<string, string> = { CLOSED_PROFIT: "chiuso in profitto", CLOSED_LOSS: "chiuso in perdita" };

/**
 * L'ANTEPRIMA E LA CONFERMA. Sul ciclo chiuso: netto prima e dopo, lo stato,
 * e lo split — le quote da confermare sul netto nuovo (proposte dalla
 * percentuale congelata, modificabili), o l'avviso che verranno stornate.
 */
function ConfirmPanel({ cycleId, sessionId, c, onBack, onDone }: { cycleId: string; sessionId?: string; c: Confirm; onBack: () => void; onDone: () => void }) {
  const refresh = useRefreshDirectory();
  const p = c.preview;
  const cur = p.cycle.currency;
  const s = p.split;
  const servonoQuote = s.action === "REQUIRED";
  const puoiRegistrare = s.action === "MISSING" && !!s.proposed;
  const [registra, setRegistra] = useState(servonoQuote);
  const [client, setClient] = useState(s.proposed ? String(s.proposed.client) : "");
  const [manager, setManager] = useState(s.proposed ? String(s.proposed.manager) : "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const conferma = async () => {
    let split: { client: number; manager: number; pct: string } | undefined;
    if ((servonoQuote || (puoiRegistrare && registra)) && p.cycle.netAfter !== null) {
      const cl = Number(decimalInput(client) ?? NaN);
      const mg = Number(decimalInput(manager) ?? NaN);
      if (!Number.isFinite(cl) || !Number.isFinite(mg) || cl < 0 || mg < 0) return setError("Scrivi le due quote come numeri non negativi");
      if (Math.abs(cl + mg - p.cycle.netAfter) > 0.005) return setError(`Le due quote devono sommare ${money(p.cycle.netAfter, cur)}`);
      split = { client: cl, manager: mg, pct: (p.cycle.netAfter > 0 ? cl / p.cycle.netAfter : 0).toFixed(6) };
    }
    setBusy(true);
    setError(null);
    try {
      if (c.op === "ADD") await api.cycles.addRebalance(cycleId, { amount: c.amount, cause: c.cause, sessionId: sessionId ?? null, manualFxRate: c.manualFxRate, split });
      else await api.cycles.removeRebalance(cycleId, c.id, { reason: c.reason, split });
      await refresh();
      onDone();
    } catch (e) {
      // Il numero può essere cambiato fra anteprima e conferma: il messaggio del backend lo dice.
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-xl space-y-2 rounded border border-line bg-raised/50 p-3 text-sm">
      {p.cycle.frozen ? (
        <>
          <p className="text-ink">
            Netto del ciclo: {money(p.cycle.netBefore, cur)} → <span className={signClass(p.cycle.netAfter ?? 0)}>{money(p.cycle.netAfter, cur)}</span>
            <span className="text-muted">
              {" "}
              (di cui ribilanciamenti {money(p.cycle.rebalancesBefore, cur)} → {money(p.cycle.rebalancesAfter, cur)})
            </span>
          </p>
          {p.cycle.stateAfter !== p.cycle.stateBefore && (
            <p className="text-ink">
              Il ciclo passa da {STATE_LABEL[p.cycle.stateBefore] ?? p.cycle.stateBefore} a {STATE_LABEL[p.cycle.stateAfter] ?? p.cycle.stateAfter}.
            </p>
          )}
        </>
      ) : (
        <p className="text-ink">
          Ribilanciamenti del ciclo: {money(p.cycle.rebalancesBefore, cur)} → {money(p.cycle.rebalancesAfter, cur)}. Il ciclo è aperto: entreranno nel netto alla
          chiusura.
        </p>
      )}

      {s.action === "REVERSED" && s.warning && <p className="rounded border border-warn/40 bg-warn/5 px-3 py-2 text-ink">{s.warning}</p>}
      {s.before && s.action !== "REVERSED" && (
        <p className="text-muted">
          Split registrato: cliente {money(s.before.client, cur)} · gestore {money(s.before.manager, cur)}
        </p>
      )}
      {puoiRegistrare && (
        <label className="flex items-center gap-2 text-ink">
          <input type="checkbox" checked={registra} onChange={(e) => setRegistra(e.target.checked)} />
          Nessuna quota registrata: registra lo split sul netto nuovo
        </label>
      )}
      {(servonoQuote || (puoiRegistrare && registra)) && s.proposed && (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={`Quota cliente (${cur})`} hint={`Proposta: ${percent(s.proposed.pctShown, 0)} del netto nuovo`}>
            {(id) => <input id={id} inputMode="decimal" value={client} onChange={(e) => setClient(e.target.value)} className={inputClass} />}
          </Field>
          <Field label={`Quota gestore (${cur})`}>
            {(id) => <input id={id} inputMode="decimal" value={manager} onChange={(e) => setManager(e.target.value)} className={inputClass} />}
          </Field>
        </div>
      )}
      <ErrorNote>{error}</ErrorNote>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onBack}>
          Indietro
        </Button>
        <Button size="sm" variant="primary" disabled={busy} onClick={() => void conferma()}>
          {busy ? "Salvataggio…" : c.op === "ADD" ? "Conferma il ribilanciamento" : "Elimina il ribilanciamento"}
        </Button>
      </div>
    </div>
  );
}
